/**
 * 科创服务新版配置（设计稿 + home_new 科创服务切图）
 * 图片统一走 service_new/aliases 短英文名
 *
 * 切图映射（aliases）:
 * banner-bg ← kechuangfuwu/head-bg
 * entry-card-bg ← home_new service-card-bg
 * entry-demo-base ← service-base
 * entry-instrument ← service-instrument
 * entry-org ← service-org
 * entry-product ← service-product
 * icon-policy-tech ← policy-tech-ico
 * icon-policy-interpret ← policy-interpret-ico
 * title-slash ← label_b_line
 */
import { deepCloneConfig } from '@/utils/deepCloneConfig'
import { applyPortalZhToRu } from '@/utils/applyPortalZhToRu'
import { getCurrentLocale } from '@/utils/i18nText'
import { L, resolveLocaleImages } from '@/utils/localeAsset'

const A = (name) => L(`service_new/aliases/${name}`)
const Nav = (name) => L(`home_new/aliases/${name}`)

const zhCN = {
  siteInfo: {
    title: '杨凌农业科技成果转化综合信息服务平台',
    logo: L('shouye/web-logo.png'),
    loginText: '登录',
    registerText: '注册',
    logoutText: '退出'
  },
  breadcrumb: {
    home: '首页',
    current: '科创服务'
  },
  header: {
    titleMain1: '科创',
    titleMain2: '服务',
    titleSub: '构建科创生态 服务产业发展',
    bannerBg: A('banner-bg.png')
  },
  modules: [
    { id: 'home', name: '首页', enName: 'HOME', icon: Nav('nav-home.png'), link: '/index' },
    { id: 'innovation', name: '走进杨凌', enName: 'YANGLING', icon: Nav('nav-innovation.png'), link: '/innovation' },
    { id: 'supply', name: '供需中心', enName: 'SUPPLY & DEMAND', icon: Nav('nav-supply.png'), link: '/supply-demand' },
    { id: 'evaluation', name: '价值评估', enName: 'EVALUATE', icon: Nav('nav-evaluation.png'), link: '/value-assessment' },
    { id: 'manager', name: '技术经理人', enName: 'MANAGER', icon: Nav('nav-manager.png'), link: '/manager' },
    { id: 'transaction', name: '交易中心', enName: 'TRANSACTION', icon: Nav('nav-transaction.png'), link: '/transaction' },
    { id: 'service', name: '科创服务', enName: 'SERVICE', icon: Nav('nav-service.png'), link: '/innovation-service' },
    { id: 'talent', name: '科技人才', enName: 'TALENT', icon: Nav('nav-talent.png'), link: '/talent' },
    { id: 'enterprise', name: '农业企业', enName: 'ENTERPRISE', icon: Nav('nav-enterprise.png'), link: '/agriculture-enterprise' }
  ],
  functionEntries: [
    {
      id: 'demo-base',
      key: 'demoBase',
      title: '示范基地',
      subtitle: 'DEMONSTRATION BASE',
      icon: A('entry-demo-base.png'),
      path: '/innovation/demo-base'
    },
    {
      id: 'instruments',
      key: 'instrument',
      title: '科研仪器',
      subtitle: 'INSTRUMENTS',
      icon: A('entry-instrument.png'),
      path: '/innovation/instruments'
    },
    {
      id: 'institution',
      key: 'org',
      title: '服务机构',
      subtitle: 'SERVICE INSTITUTION',
      icon: A('entry-org.png'),
      path: '/service-institution'
    },
    {
      id: 'product',
      key: 'product',
      title: '服务商品',
      subtitle: 'SERVICE GOODS',
      icon: A('entry-product.png'),
      path: '/service-product'
    }
  ],
  entryCardBg: A('entry-card-bg.png'),
  newsSections: {
    techPolicy: {
      title: '科技政策',
      subtitle: 'TECHNOLOGY POLICY',
      moreText: '更多+',
      type: 3,
      icon: A('icon-policy-tech.png')
    },
    policyInterpret: {
      title: '政策解读',
      subtitle: 'POLICY INTERPRETATION',
      moreText: '更多+',
      type: 4,
      icon: A('icon-policy-interpret.png')
    }
  },
  icons: {
    titleSlash: A('title-slash.png')
  },
  list: {
    emptyTip: '待接口提供',
    emptyList: '暂无数据'
  },
  pagination: {
    pageInfo: '共{total}条，每页显示{pageSize}条',
    prevPage: '上一页',
    nextPage: '下一页',
    loading: '加载中...'
  },
  footer: {
    copyright: '© 2026 杨凌农业科技成果转化综合信息服务平台 版权所有',
    support: '技术支持：浪潮软件'
  }
}

const enUS = {
  siteInfo: {
    title: 'Yangling Agricultural Science & Technology Achievement Transformation Platform',
    logo: L('shouye/web-logo.png'),
    loginText: 'Login',
    registerText: 'Register',
    logoutText: 'Logout'
  },
  breadcrumb: {
    home: 'Home',
    current: 'Innovation Service'
  },
  header: {
    titleMain1: 'Innovation',
    titleMain2: 'Service',
    titleSub: 'Building an innovation ecosystem to serve industrial development',
    bannerBg: A('banner-bg.png')
  },
  modules: [
    { id: 'home', name: 'Home', enName: 'HOME', icon: Nav('nav-home.png'), link: '/index' },
    { id: 'innovation', name: 'Yangling', enName: 'YANGLING', icon: Nav('nav-innovation.png'), link: '/innovation' },
    { id: 'supply', name: 'Supply & Demand', enName: 'SUPPLY & DEMAND', icon: Nav('nav-supply.png'), link: '/supply-demand' },
    { id: 'evaluation', name: 'Evaluation', enName: 'EVALUATE', icon: Nav('nav-evaluation.png'), link: '/value-assessment' },
    { id: 'manager', name: 'Managers', enName: 'MANAGER', icon: Nav('nav-manager.png'), link: '/manager' },
    { id: 'transaction', name: 'Transaction', enName: 'TRANSACTION', icon: Nav('nav-transaction.png'), link: '/transaction' },
    { id: 'service', name: 'Service', enName: 'SERVICE', icon: Nav('nav-service.png'), link: '/innovation-service' },
    { id: 'talent', name: 'Talent', enName: 'TALENT', icon: Nav('nav-talent.png'), link: '/talent' },
    { id: 'enterprise', name: 'Enterprises', enName: 'ENTERPRISE', icon: Nav('nav-enterprise.png'), link: '/agriculture-enterprise' }
  ],
  functionEntries: [
    {
      id: 'demo-base',
      key: 'demoBase',
      title: 'Demo Base',
      subtitle: 'DEMONSTRATION BASE',
      icon: A('entry-demo-base.png'),
      path: '/innovation/demo-base'
    },
    {
      id: 'instruments',
      key: 'instrument',
      title: 'Instruments',
      subtitle: 'INSTRUMENTS',
      icon: A('entry-instrument.png'),
      path: '/innovation/instruments'
    },
    {
      id: 'institution',
      key: 'org',
      title: 'Institutions',
      subtitle: 'SERVICE INSTITUTION',
      icon: A('entry-org.png'),
      path: '/service-institution'
    },
    {
      id: 'product',
      key: 'product',
      title: 'Service Goods',
      subtitle: 'SERVICE GOODS',
      icon: A('entry-product.png'),
      path: '/service-product'
    }
  ],
  entryCardBg: A('entry-card-bg.png'),
  newsSections: {
    techPolicy: {
      title: 'Tech Policy',
      subtitle: 'TECHNOLOGY POLICY',
      moreText: 'More+',
      type: 3,
      icon: A('icon-policy-tech.png')
    },
    policyInterpret: {
      title: 'Interpretation',
      subtitle: 'POLICY INTERPRETATION',
      moreText: 'More+',
      type: 4,
      icon: A('icon-policy-interpret.png')
    }
  },
  icons: zhCN.icons,
  list: {
    emptyTip: '待接口提供',
    emptyList: 'No data'
  },
  pagination: {
    pageInfo: '{total} items, {pageSize} per page',
    prevPage: 'Prev',
    nextPage: 'Next',
    loading: 'Loading...'
  },
  footer: {
    copyright: '© 2026 Yangling Agricultural Science & Technology Marketplace. All rights reserved.',
    support: 'Technical support: Inspur Software'
  }
}

export function getInnovationServiceNewConfig(locale = getCurrentLocale()) {
  let raw
  if (locale === 'en-US') raw = deepCloneConfig(enUS)
  else if (locale === 'ru-RU') raw = applyPortalZhToRu(deepCloneConfig(zhCN))
  else raw = deepCloneConfig(zhCN)
  return resolveLocaleImages(raw, locale)
}
